// src/pages/Login.jsx
import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Login as LoginIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  PersonAdd as PersonAddIcon
} from "@mui/icons-material";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Check for messages from registration or other pages
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      if (location.state?.email) {
        setFormData(prev => ({ ...prev, email: location.state.email }));
      }
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value.trim()
    }));
    
    if (error) setError("");
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError("Please fill in all fields");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Clear any existing tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('user_data');
    
    setLoading(true);
    setError("");
    setSuccessMessage("");

    const requestBody = {
      email: formData.email,
      password: formData.password,
      remember_me: rememberMe
    };

    console.log("=== LOGIN REQUEST DEBUG ===");
    console.log("URL:", "/api/auth/login");
    console.log("Request body:", requestBody);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(requestBody)
      });

      console.log("Response status:", response.status);
      const data = await response.json();
      console.log("Response data:", data);

      if (response.ok) {
        // Store authentication data
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("token_type", data.token_type || "Bearer");
        
        if (data.user) {
          localStorage.setItem("user_data", JSON.stringify(data.user));
        }

        // Handle remember me functionality
        if (rememberMe) {
          localStorage.setItem("remember_email", formData.email);
        } else {
          localStorage.removeItem("remember_email");
        }

        try {
          // Decode JWT token to get user info
          const payload = JSON.parse(atob(data.access_token.split('.')[1]));
          console.log("Decoded payload:", payload);
          
          const isAdmin = payload.is_admin || false;
          const userRole = payload.role || 'user';
          
          // Navigate based on user role or continue with expert assessment
          if (isAdmin) {
            navigate("/admin/dashboard");
          } else {
            navigate("/organisation-details", { 
              state: { 
                assessmentType: 'expert',
                userData: data.user 
              }
            });
          }
        } catch (decodeError) {
          console.error("Error decoding token:", decodeError);
          // Fallback navigation
          navigate("/organisation-details", { 
            state: { 
              assessmentType: 'expert',
              userData: data.user 
            }
          });
        }
      } else {
        setError(data.detail || data.message || `Login failed: ${response.status}`);
      }
    } catch (err) {
      console.error("Network error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("remember_email");
    if (rememberedEmail) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, []);

  return (
    <Box 
      sx={{ 
        minHeight: "100vh", 
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 2 }}>
        <Container>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative'
          }}>
            <IconButton 
              onClick={() => navigate('/')}
              sx={{ 
                color: 'white', 
                position: 'absolute',
                left: 0
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LoginIcon sx={{ mr: 2, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Sign In to RAISE Index
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content - Centered */}
      <Box 
        sx={{ 
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center",
            width: "100%"
          }}>
            <Paper
              elevation={3}
              sx={{
                width: "100%",
                maxWidth: 450,
                padding: 4,
                borderRadius: 2,
                textAlign: "center" // Center all content inside paper
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  Welcome Back
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Sign in to access expert-led Responsible AI assessments
                </Typography>
              </Box>

              {successMessage && (
                <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                  {successMessage}
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleLogin}>
                <TextField
                  name="email"
                  label="Email Address"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={loading}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  required
                />

                <TextField
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  variant="outlined"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={loading}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          disabled={loading}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  required
                />

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  mb: 3,
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        disabled={loading}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Remember me
                      </Typography>
                    }
                  />
                  
                  <Button
                    variant="text"
                    size="small"
                    disabled={loading}
                    sx={{ 
                      color: 'primary.main',
                      textTransform: 'none',
                      fontSize: '0.875rem'
                    }}
                    onClick={() => {
                      setError("Forgot password feature coming soon. Please contact support.");
                    }}
                  >
                    Forgot password?
                  </Button>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  size="large"
                  sx={{ 
                    mb: 2,
                    py: 1.5,
                    position: "relative"
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  or
                </Typography>
              </Divider>

              <Button
                component={Link}
                to="/register"
                fullWidth
                variant="outlined"
                disabled={loading}
                size="large"
                startIcon={<PersonAddIcon />}
                sx={{ mb: 2 }}
              >
                Create New Account
              </Button>

              <Button
                onClick={() => navigate('/')}
                fullWidth
                sx={{ 
                  mt: 1,
                  color: 'text.secondary',
                  textTransform: 'none'
                }}
                disabled={loading}
              >
                Back to Home
              </Button>
            </Paper>
          </Box>

          {/* Info Section - Centered */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                maxWidth: 450, 
                width: '100%',
                bgcolor: 'background.accent',
                border: '1px solid',
                borderColor: 'divider',
                textAlign: 'center'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Expert-led Assessment Benefits
              </Typography>
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ✓ Comprehensive Responsible AI evaluation
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ✓ Personalized expert consultation
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  ✓ Detailed implementation roadmap
                </Typography>
                <Typography variant="body2">
                  ✓ Industry benchmarking and best practices
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}